import { useEffect, useRef } from 'react';

function buildShortName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length <= 12) return trimmed;
  return trimmed.slice(0, 12).trim();
}

/** Inject a same-origin web app manifest using the configured application name. */
export function usePwaManifest(appName: string): void {
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const name = appName.trim();
    if (!name) return;

    const publicUrl = process.env.PUBLIC_URL || '';
    const manifest = {
      short_name: buildShortName(name),
      name,
      icons: [
        {
          src: `${publicUrl}/favicon.svg`,
          sizes: 'any',
          type: 'image/svg+xml',
          purpose: 'any',
        },
      ],
      start_url: '.',
      display: 'standalone',
      theme_color: '#0f172a',
      background_color: '#0f172a',
    };

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
    const url = URL.createObjectURL(blob);

    let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    objectUrlRef.current = url;
    link.href = url;

    let appleTitle = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement('meta');
      appleTitle.name = 'apple-mobile-web-app-title';
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = buildShortName(name);

    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [appName]);
}
